import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { CamDateInput } from '../types';

export const WEEK_STARTS_ON = 1;

export const MS_PER_MINUTE = 60 * 1000;

export const MS_PER_HOUR = 60 * MS_PER_MINUTE;

export const MS_PER_DAY = 24 * MS_PER_HOUR;

export const toDate = (value: CamDateInput): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  if (isNaN(parsed.getTime())) {
    const fallback = new Date(value as any);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  return parsed;
};

export const startOfWeekMonday = (date: Date): Date => startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });

export const endOfWeekMonday = (date: Date): Date => endOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });

export const addUnit = (date: Date, unit: string, amount: number): Date => {
  switch (unit) {
    case 'minute':
      return addMinutes(date, amount);
    case 'hour':
      return addHours(date, amount);
    case 'day':
      return addDays(date, amount);
    case 'week':
      return addWeeks(date, amount);
    case 'month':
      return addMonths(date, amount);
    case 'year':
      return addYears(date, amount);
    default:
      return addHours(date, amount);
  }
};

export const dayRange = (date: Date): { end: Date; start: Date } => ({
  end: addDays(startOfDay(date), 1),
  start: startOfDay(date),
});

export const weekRange = (date: Date): { end: Date; start: Date } => ({
  end: addDays(startOfWeekMonday(date), 7),
  start: startOfWeekMonday(date),
});

export const monthRange = (date: Date): { end: Date; start: Date } => ({
  end: addDays(startOfDay(endOfMonth(date)), 1),
  start: startOfMonth(date),
});

export const monthGridRange = (date: Date): { end: Date; start: Date } => ({
  end: addDays(startOfWeekMonday(endOfMonth(date)), 7),
  start: startOfWeekMonday(startOfMonth(date)),
});

export const yearRange = (date: Date): { end: Date; start: Date } => ({
  end: addYears(startOfYear(date), 1),
  start: startOfYear(date),
});

export const overlaps = (startA: Date, endA: Date, startB: Date, endB: Date): boolean =>
  startA.getTime() < endB.getTime() && endA.getTime() > startB.getTime();

export const clamp = (value: Date, min: Date, max: Date): Date => {
  if (value.getTime() < min.getTime()) {
    return min;
  }
  if (value.getTime() > max.getTime()) {
    return max;
  }
  return value;
};

export const snapToMinutes = (date: Date, increment: number): Date => {
  if (increment <= 0) {
    return date;
  }
  const step = increment * MS_PER_MINUTE;
  return new Date(Math.round(date.getTime() / step) * step);
};

export const eachDay = (start: Date, end: Date): Date[] => {
  const days: Date[] = [];
  let cursor = startOfDay(start);
  while (cursor.getTime() < end.getTime()) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

export const minutesSinceStartOfDay = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
